import dayjs from 'dayjs'
import isSameOrAfter from 'dayjs/plugin/isSameOrAfter'

import { CommonPayNormalLabelIcon, CommonPayNormalNewLabelIcon, MissionGiftIcon } from '@local/assets/icons'

import { gql } from '@web/gql'
import {
  MissionGroupsToMissionCards_MissionFragment,
  MissionGroupsToMissionCards_MissionGroupFragment,
  MissionGroupType,
} from '@web/gql/graphql'
import { extractByTypename, isTypename } from '@web/libs/apollo/typeGuard'

import { MissionCardProps, MissionCardDisplayType, MissionCard } from './MissionCard'

dayjs.extend(isSameOrAfter)

export const INFINITY_STAMP_COUNT_TO_COMPLETE = 99999 // 무한형 미션인 경우 stampCountToComplete

const MissionGroupTypeToMissionCardDisplayMap: Record<MissionGroupType, MissionCardDisplayType> = {
  PROFILE_BORDER: MissionCardDisplayType.STAMP_BOARD,
  REVIEW_MASTER: MissionCardDisplayType.STAMP_BOARD,
  REVIEW_LOTTERY: MissionCardDisplayType.STEPPING,
  REGION_REVIEW: MissionCardDisplayType.GAUGE,
  PLACE_CATEGORY_REVIEW: MissionCardDisplayType.GAUGE,
  PHOTO_REVIEW: MissionCardDisplayType.STAMP_BOARD,
  REVIEW_VIEWS: MissionCardDisplayType.STAMP_BOARD,
  NATURE_CATEGORY_REVIEW: MissionCardDisplayType.STAMP_BOARD,
  VIDEO_REVIEW: MissionCardDisplayType.STAMP_BOARD,
}

const groupStampMissionGroupTypes = [MissionGroupType.PlaceCategoryReview, MissionGroupType.RegionReview]

export const missionGroupsToMissionCards = (
  missionGroups: MissionGroupsToMissionCards_MissionGroupFragment[],
): MissionCardProps[] => {
  const missionCards: MissionCardProps[] = []

  missionGroups.forEach(missionGroup => {
    const { SingleMissionGroup, StepMissionGroup } = extractByTypename(missionGroup)
    const { weight, missionActivity, type: missionType } = missionGroup
    const { StampMissionActivity, StampGroupMissionActivity } = extractByTypename(missionActivity)
    const stampGroups: MissionCardProps['stampGroups'] = []

    const getReward = (
      reward: MissionGroupsToMissionCards_MissionFragment['reward'],
    ): Pick<MissionCardProps['reward'], 'name' | 'Icon' | 'type' | 'imageUrl'> => {
      const { NPayMissionReward, ProfileBorderMissionReward } = extractByTypename(reward)

      if (NPayMissionReward)
        return {
          type: 'npay',
          name: `포인트 ${NPayMissionReward?.amount.toLocaleString()}원`,
          Icon: dayjs().isSameOrAfter('2023-10-18 12:00:00') ? CommonPayNormalNewLabelIcon : CommonPayNormalLabelIcon,
        }

      return {
        type: 'profileBorder',
        name: ProfileBorderMissionReward?.decoration.name ?? '',
        Icon: MissionGiftIcon,
        imageUrl: ProfileBorderMissionReward?.decoration.imageUrl ?? undefined,
      }
    }

    if (StampMissionActivity) {
      StampMissionActivity.stamps.forEach(stamp => {
        // 최신 리뷰의 대표 이미지를 가져옵니다.
        const coverMediaItem = stamp.proof?.reviewGroup?.mediaItems.find(mediaItem => mediaItem.isCover)

        const { ImageItem, VideoItem } = extractByTypename(coverMediaItem)

        stampGroups.push({
          image: ImageItem?.url ?? VideoItem?.thumbnail,
          stamps: [stamp],
        })
      })
    } else if (StampGroupMissionActivity) {
      StampGroupMissionActivity.stampGroups.forEach(_stampGroup => {
        const proofs = _stampGroup.stamps.map(stamp => stamp.proof)

        // 이미지가 작성된 리뷰(스탬프)중에 가장 최신 리뷰를 찾습니다.
        const latestProofWithMedia = proofs
          .reverse()
          .find(proof => isTypename(proof, 'ReviewGroupProof') && (proof.reviewGroup?.mediaItems.length ?? 0) > 0)

        // 최신 리뷰의 대표 이미지를 가져옵니다.
        const coverMediaItem = latestProofWithMedia?.reviewGroup?.mediaItems.find(mediaItem => mediaItem.isCover)

        const { ImageItem, VideoItem } = extractByTypename(coverMediaItem)

        stampGroups.push({
          name: _stampGroup.category.name,
          stamps: _stampGroup.stamps,
          image: _stampGroup.category.emojiUrl ?? ImageItem?.url ?? VideoItem?.thumbnail,
        })
      })
    }

    if (SingleMissionGroup) {
      const missionState = missionActivity?.missionStates[0]

      missionCards.push({
        ...SingleMissionGroup.mission,
        missionGroupType: SingleMissionGroup.type,
        missionGroupId: SingleMissionGroup.id,
        startDateTime: dayjs(SingleMissionGroup.startDateTime),
        endDateTime: dayjs(SingleMissionGroup.endDateTime),
        label: SingleMissionGroup.mission.missionLabel,
        displayType: MissionGroupTypeToMissionCardDisplayMap[missionType],
        weight,
        guide: SingleMissionGroup.mission.tooltip,
        iconUrl: SingleMissionGroup.mission.iconUrl,
        stampGroups,
        ...(missionState?.complete && {
          complete: {
            dateTime: missionState?.complete.dateTime,
          },
        }),
        isRewarded: missionState?.isRewarded,
        rewardedDateTime: missionState?.rewardedDateTime,
        reward: {
          ...SingleMissionGroup.mission.reward,
          ...getReward(SingleMissionGroup.mission.reward),
        },
        displayStampGroupCount: stampGroups.length,
        isBeforeStart: stampGroups.length === 0,
        isInfiniteMission: SingleMissionGroup.mission.stampCountToComplete === INFINITY_STAMP_COUNT_TO_COMPLETE,
        isGroupStamp: groupStampMissionGroupTypes.includes(SingleMissionGroup.type),
      })
    } else if (StepMissionGroup) {
      const missionCardGroup: MissionCardProps[] = []
      let isNotStartedStep = false

      for (let i = 0; i < StepMissionGroup.missions.length; i++) {
        const mission = StepMissionGroup.missions[i]

        const prevMission = StepMissionGroup.missions[i - 1]
        const diffStampGroupCount = stampGroups.length - (prevMission?.stampCountToComplete ?? 0)

        const missionState = missionActivity?.missionStates.find(state => state.mission.id === mission.id)

        const missionCard: MissionCardProps = {
          ...mission,
          missionGroupType: StepMissionGroup.type,
          missionGroupId: StepMissionGroup.id,
          startDateTime: dayjs(StepMissionGroup.startDateTime),
          endDateTime: dayjs(StepMissionGroup.endDateTime),
          label: mission.missionLabel,
          displayType: MissionGroupTypeToMissionCardDisplayMap[missionType],
          weight,
          stampGroups,
          guide: mission.tooltip,
          iconUrl: mission.iconUrl,
          missionCardGroup,
          ...(missionState?.complete && {
            complete: {
              dateTime: missionState.complete.dateTime,
            },
          }),
          isRewarded: missionState?.isRewarded,
          rewardedDateTime: missionState?.rewardedDateTime,
          reward: {
            ...mission.reward,
            ...getReward(mission.reward),
          },
          displayStampGroupCount: diffStampGroupCount > 0 ? diffStampGroupCount : 0,
          isBeforeStart: stampGroups.length === 0,
          isInfiniteMission: mission.stampCountToComplete === INFINITY_STAMP_COUNT_TO_COMPLETE,
          isGroupStamp: groupStampMissionGroupTypes.includes(StepMissionGroup.type),
        }

        missionCardGroup.push(missionCard)

        const completedStampGroups = MissionCard.getCompleteMissionStampGroups(missionCard)

        // 진행중인 단계까지만 포함
        if (!isNotStartedStep) {
          missionCards.push(missionCard)
        }

        if (completedStampGroups.length < mission.stampCountToComplete) {
          isNotStartedStep = true
        }
      }
    }
  })

  return missionCards
}

missionGroupsToMissionCards.fragment = gql(`
  fragment MissionGroupsToMissionCards_Mission on Mission {
    id
    description
    name
    tooltip
    iconUrl
    missionLabel {
      text
      type
    }
    previewText
    nextStepPreview {
      text
      iconUrl
    }
    stampCountToComplete
    itemCountToStamp
    reward {
      type
      selectMethod
      ... on NPayMissionReward {
        amount
        winnerCount
      }
      ... on ProfileBorderMissionReward {
        decoration {
          name
          imageUrl
        }
      }
    }
  }

  fragment MissionGroupsToMissionCards_Stamp on MissionStamp {
    proof {
      id
      ... on ReviewGroupProof {
        reviewGroup {
          id
          mediaItems {
            id
            type
            ... on ImageItem {
              url
            }
            ... on VideoItem {
              thumbnail
            }
            isCover
          }
        }
      }
    }
    createdDateTime
  }

  fragment MissionGroupsToMissionCards_MissionGroup on MissionGroup {
    id
    type
    weight
    startDateTime
    endDateTime
    ... on SingleMissionGroup {
      id
      mission {
        ...MissionGroupsToMissionCards_Mission
      }
    }
    ... on StepMissionGroup {
      id
      missions {
        ...MissionGroupsToMissionCards_Mission
      }
    }
    missionActivity {
      missionStates {
        mission {
          id
        }
        complete {
          dateTime
        }
        isRewarded
        rewardedDateTime
      }
      ... on StampGroupMissionActivity {
        id
        stampGroups {
          category {
            code
            name
            emojiUrl
          }
          stamps {
            ...MissionGroupsToMissionCards_Stamp
          }
        }
      }
      ... on StampMissionActivity {
        id
        stamps {
          ...MissionGroupsToMissionCards_Stamp
        }
      }
    }
  }
`)
